import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'providers/AllProviders';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AddColumn,
  AddRow,
  appendCsv,
  createWorksheet,
  deleteColumn,
  deleteRow,
  deleteWorksheet,
  exucuteWorkflow,
  getColumns,
  getFilteredRows,
  getRows,
  getWorkflow,
  getWorksheet,
  getWorksheets,
  UpdateCell,
  UpdateColumn,
  UpdateRow,
  updateWorksheet
} from './api';
import {
  AddColumnRequest,
  AddRowRequest,
  AppendCsv,
  ColumnResponse,
  RowsRequest,
  UpdateCellRequest,
  WorksheetRequest,
  WorksheetResponse
} from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import * as urls from 'routes/config';

export function useGetWorksheets() {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useQuery<{ data: WorksheetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['worksheets', workspaceId, agentId],
    queryFn: () => getWorksheets(workspaceId!, agentId!),
    enabled: Boolean(workspaceId && agentId)
  });
}

export function useGetWorksheet() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useQuery<WorksheetResponse>({
    queryKey: ['worksheet', workspaceId, agentId, worksheetId],
    queryFn: () => getWorksheet(workspaceId!, agentId!, worksheetId!),
    enabled: Boolean(workspaceId && agentId && worksheetId)
  });
}

export function useGetColumns() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useQuery<{ data: ColumnResponse[]; meta: MetaApiResponse }>({
    queryKey: ['columns', workspaceId, agentId, worksheetId],
    queryFn: () => getColumns(workspaceId!, agentId!, worksheetId!),
    enabled: Boolean(workspaceId && agentId && worksheetId)
  });
}

export function useGetRows(filters?: Record<string, any>, sorting?: Record<string, any>) {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  const queryParams = { ...filters, ...sorting };

  return useQuery<any>({
    queryKey: ['rows', workspaceId, agentId, worksheetId, queryParams],
    queryFn: () =>
      queryParams
        ? getFilteredRows(workspaceId!, agentId!, worksheetId!, queryParams)
        : getRows(workspaceId!, agentId!, worksheetId!),
    enabled: Boolean(workspaceId && agentId && worksheetId)
  });
}

export function useCreateWorksheet() {
  const navigate = useNavigate();
  const { workspaceId, agentId } = useParams<{
    workspaceId: string;
    agentId: string;
  }>();

  return useMutation({
    mutationFn: () => createWorksheet(workspaceId!, agentId!),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        navigate(`${urls.worksheetBase(workspaceId, agentId)}/${data?.id}`);
      }
    }
  });
}

export function useUpdateWorksheet() {
  const navigate = useNavigate();
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: WorksheetRequest) => updateWorksheet(workspaceId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        navigate(-1);
      }
    }
  });
}

export function useAddColumn(worksheet?: string) {
  const navigate = useNavigate();
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (payload: AddColumnRequest) => AddColumn(workspaceId!, agentId!, worksheetId! ?? worksheet, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        navigate(-1);
      }
    }
  });
}

export function useAddRow() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: AddRowRequest) => AddRow(workspaceId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['rows', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateCell() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ rowId, columnId, payload }: { rowId: string; columnId: number; payload: UpdateCellRequest }) =>
      UpdateCell(workspaceId!, agentId!, worksheetId!, rowId, columnId, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['rows', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateColumn() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ columnId, payload }: { columnId: string; payload: object }) =>
      UpdateColumn(workspaceId!, agentId!, worksheetId!, columnId, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateRow() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ rowId, payload }: { rowId: string; payload: object }) =>
      UpdateRow(workspaceId!, agentId!, worksheetId!, rowId, payload)
  });
}

export function useDeleteAgentWorksheet() {
  const { workspaceId, agentId } = useParams<{
    workspaceId: string;
    agentId: string;
  }>();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (worksheetId: string) => deleteWorksheet(workspaceId!, agentId!, worksheetId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['worksheets', workspaceId, agentId],
        refetchType: 'all'
      });
      navigate(`${urls.worksheetBase(workspaceId, agentId)}`);
    }
  });
}

export function useDeleteColumn() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (columnId: string) => deleteColumn(workspaceId!, agentId!, worksheetId!, columnId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['columns', workspaceId, agentId, worksheetId],
        refetchType: 'all'
      });
    }
  });
}

export function useDeleteRow() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (payload: RowsRequest) => deleteRow(workspaceId!, agentId!, worksheetId!, payload!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['columns', workspaceId, agentId, worksheetId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['worksheet', workspaceId, agentId, worksheetId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['worksheets', workspaceId, agentId],
        refetchType: 'all'
      });
    }
  });
}

export function useExecuteWorkflow() {
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: { row_id?: number | number[]; column_id: number; limit: string }) =>
      exucuteWorkflow(workspaceId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', workspaceId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', workspaceId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useCsvAppend() {
  const navigate = useNavigate();
  const { workspaceId, agentId, worksheetId } = useParams<{
    workspaceId: string;
    agentId: string;
    worksheetId: string;
    appendCsv: string;
  }>();
  return useMutation({
    mutationFn: (payload: AppendCsv) => appendCsv(workspaceId!, agentId!, worksheetId!, payload),
    onSuccess: () => {
      navigate(-1);
    }
  });
}

export function useGetWorkflow() {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<any>({
    queryKey: ['workflows', workspaceId],
    queryFn: () => getWorkflow(workspaceId!),
    enabled: Boolean(workspaceId)
  });
}
